<script setup>
import { computed, ref } from "vue";
import { segColors, formatMoney } from "../../utils/analyticsUtils.js";

const props = defineProps({
  daily: { type: Array, default: () => [] },
  monthly: { type: Array, default: () => [] },
  yearly: { type: Array, default: () => [] },
});

// Helper to calculate SVG paths for donut segments
function calculateDonutSegments(data, totalValue) {
  let cumulativePercent = 0;

  // If no data, return empty
  if (!totalValue || totalValue === 0) return [];

  return data.map((item, index) => {
    const value = Number(item.value || 0);
    const percent = value / totalValue;

    // Start and end angles in radians (shifted by -PI/2 to start at top)
    const startAngle = 2 * Math.PI * cumulativePercent - Math.PI / 2;
    const endAngle = 2 * Math.PI * (cumulativePercent + percent) - Math.PI / 2;

    cumulativePercent += percent;

    // SVG Path calculation
    // Outer radius = 1, Inner radius = 0.6 (hole)

    // Handle 100% case (full circle)
    if (percent > 0.9999) {
      // Draw as two 180 degree arcs to form a full ring, connected by a "cut" line
      // This ensures the fill rule works correctly for the hole
      return {
        ...item,
        d: [
          "M 0 -1",                   // Move to top outer
          "A 1 1 0 1 1 0 1",          // Arc to bottom outer (CW)
          "A 1 1 0 1 1 0 -1",         // Arc to top outer (CW)
          "L 0 -0.6",                 // Line to top inner (The Cut)
          "A 0.6 0.6 0 1 0 0 0.6",    // Arc to bottom inner (CCW)
          "A 0.6 0.6 0 1 0 0 -0.6",   // Arc to top inner (CCW)
          "Z"                         // Close path (Line back to top outer)
        ].join(" "),
        color: item.color
      };
    }

    const x1 = Math.cos(startAngle);
    const y1 = Math.sin(startAngle);
    const x2 = Math.cos(endAngle);
    const y2 = Math.sin(endAngle);

    // Large arc flag
    const largeArcFlag = percent > 0.5 ? 1 : 0;

    // Path string
    // M startX startY A 1 1 0 largeArc 1 endX endY L endX*0.6 endY*0.6 A 0.6 0.6 0 largeArc 0 startX*0.6 startY*0.6 Z
    const d = [
      `M ${x1} ${y1}`, // Move to outer start
      `A 1 1 0 ${largeArcFlag} 1 ${x2} ${y2}`, // Arc to outer end
      `L ${x2 * 0.6} ${y2 * 0.6}`, // Line to inner end
      `A 0.6 0.6 0 ${largeArcFlag} 0 ${x1 * 0.6} ${y1 * 0.6}`, // Arc to inner start (reverse)
      "Z" // Close path
    ].join(" ");

    return {
      ...item,
      d,
      color: item.color
    };
  });
}

// Computed Data
const dailyTotal = computed(() => props.daily.reduce((s, d) => s + Number(d.total || 0), 0));
const monthlyTotal = computed(() => props.monthly.reduce((s, m) => s + Number(m.total || 0), 0));
const yearlyTotal = computed(() => props.yearly.reduce((s, y) => s + Number(y.total || 0), 0));

const dailyDisplayedTotal = computed(() => {
  const last = props.daily.slice(-7);
  return last.reduce((s, d) => s + Number(d.total || 0), 0);
});

const dailyChartSegments = computed(() => {
  const last = props.daily.slice(-7);
  const colors = segColors(last.length);
  const data = last.map((d, i) => ({
    label: new Date(d.date).toLocaleDateString(),
    value: Number(d.total || 0),
    color: colors[i],
  }));
  return calculateDonutSegments(data, dailyDisplayedTotal.value);
});


const monthlySegments = computed(() => {
  const colors = segColors(props.monthly.length);
  const data = props.monthly.map((m, i) => ({
    label: m.label,
    value: Number(m.total || 0),
    color: colors[i],
  }));
  return calculateDonutSegments(data, monthlyTotal.value);
});

const yearlySegments = computed(() => {
  const colors = segColors(props.yearly.length);
  const data = props.yearly.map((y, i) => ({
    label: y.year,
    value: Number(y.total || 0),
    color: colors[i],
  }));
  return calculateDonutSegments(data, yearlyTotal.value);
});

// Tooltip State
const hoveredPoint = ref(null);
const tooltipStyle = ref({});

function showTooltip(e, label, value) {
  const rect = e.target.getBoundingClientRect();
  hoveredPoint.value = {
    label: label,
    value: formatMoney(value),
  };
  tooltipStyle.value = {
    top: `${rect.top - 40}px`,
    left: `${rect.left + rect.width / 2}px`,
  };
}

function hideTooltip() {
  hoveredPoint.value = null;
}
</script>

<template>
  <section class="panel power-grid">
    <div class="panel-head">
      <b>Visualizaciones de ventas</b>
      <div class="panel-sub"></div>
    </div>
    <div class="viz-grid">
      <!-- Daily Chart -->
      <div class="viz-card">
        <div class="viz-title">Diario (últimos 7 días)</div>
        <div class="donut-container">
          <svg viewBox="-1.1 -1.1 2.2 2.2" class="donut-svg">
            <path v-for="(seg, i) in dailyChartSegments" :key="i" :d="seg.d" :fill="seg.color" class="donut-segment"
              @mouseenter="showTooltip($event, seg.label, seg.value)" @mouseleave="hideTooltip" />
          </svg>
          <div class="donut-center-text">
            <div class="donut-value">{{ formatMoney(dailyDisplayedTotal) }}</div>
            <div class="donut-label">Total</div>
          </div>
        </div>
        <div class="viz-legend">
          <div v-for="seg in dailyChartSegments" :key="seg.label" class="legend-item">
            <span class="legend-dot" :style="{ background: seg.color }"></span>
            <span class="legend-text">{{ seg.label }}</span>
            <span class="legend-val">{{ formatMoney(seg.value) }}</span>
          </div>
        </div>
      </div>

      <!-- Monthly Chart -->
      <div class="viz-card">
        <div class="viz-title">Mensual</div>
        <div class="donut-container">
          <svg viewBox="-1.1 -1.1 2.2 2.2" class="donut-svg">
            <path v-for="(seg, i) in monthlySegments" :key="i" :d="seg.d" :fill="seg.color" class="donut-segment"
              @mouseenter="showTooltip($event, seg.label, seg.value)" @mouseleave="hideTooltip" />
          </svg>
          <div class="donut-center-text">
            <div class="donut-value">{{ formatMoney(monthlyTotal) }}</div>
            <div class="donut-label">Total</div>
          </div>
        </div>
        <div class="viz-legend">
          <div v-for="seg in monthlySegments" :key="seg.label" class="legend-item">
            <span class="legend-dot" :style="{ background: seg.color }"></span>
            <span class="legend-text">{{ seg.label }}</span>
            <span class="legend-val">{{ formatMoney(seg.value) }}</span>
          </div>
        </div>
      </div>

      <!-- Yearly Chart -->
      <div class="viz-card">
        <div class="viz-title">Anual</div>
        <div class="donut-container">
          <svg viewBox="-1.1 -1.1 2.2 2.2" class="donut-svg">
            <path v-for="(seg, i) in yearlySegments" :key="i" :d="seg.d" :fill="seg.color" class="donut-segment"
              @mouseenter="showTooltip($event, seg.label, seg.value)" @mouseleave="hideTooltip" />
          </svg>
          <div class="donut-center-text">
            <div class="donut-value">{{ formatMoney(yearlyTotal) }}</div>
            <div class="donut-label">Total</div>
          </div>
        </div>
        <div class="viz-legend">
          <div v-for="seg in yearlySegments" :key="seg.label" class="legend-item">
            <span class="legend-dot" :style="{ background: seg.color }"></span>
            <span class="legend-text">{{ seg.label }}</span>
            <span class="legend-val">{{ formatMoney(seg.value) }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Local Tooltip Portal -->
  <Teleport to="body">
    <div v-if="hoveredPoint" class="chart-tooltip" :style="tooltipStyle">
      <div class="tt-label">{{ hoveredPoint.label }}</div>
      <div class="tt-val">{{ hoveredPoint.value }}</div>
    </div>
  </Teleport>
</template>

<style scoped>
.power-grid {
  display: grid;
  gap: 0.8rem;
}

.viz-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.8rem;
}

@media (min-width: 900px) {
  .viz-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.viz-card {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.viz-title {
  font-weight: 700;
  color: var(--text);
  text-align: center;
}

.donut-container {
  position: relative;
  width: 200px;
  height: 200px;
  margin: 0 auto;
}

.donut-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  /* Start from top */
  overflow: visible;
}

.donut-segment {
  transition: opacity 0.2s, transform 0.2s;
  cursor: pointer;
  stroke: var(--surface);
  stroke-width: 0.02;
}

.donut-segment:hover {
  opacity: 0.8;
  transform: scale(1.05);
  transform-origin: center;
}

.donut-center-text {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.donut-value {
  font-weight: 900;
  color: var(--text);
  font-size: 1.1rem;
}

.donut-label {
  color: var(--muted);
  font-size: 0.8rem;
}

.viz-legend {
  display: grid;
  gap: 0.35rem;
}

.legend-item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.4rem;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 999px;
}

.legend-text {
  color: var(--muted);
  font-size: 0.85rem;
}

.legend-val {
  font-weight: 700;
  color: var(--text);
  font-size: 0.85rem;
}

.panel {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  color: var(--text);
}

.panel-sub {
  color: var(--accent);
  font-weight: 700;
}

/* Tooltip Styles (Global/Teleported) */
.chart-tooltip {
  position: fixed;
  transform: translate(-50%, -100%);
  background: rgba(15, 23, 42, 0.9);
  color: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 0.8rem;
  pointer-events: none;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
}

.tt-label {
  font-weight: 600;
  margin-bottom: 2px;
  color: #cbd5e1;
}

.tt-val {
  font-weight: 700;
  color: #fff;
}

/* Responsive Design */
@media (max-width: 768px) {
  .viz-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.6rem;
  }

  .donut-container {
    width: 160px;
    height: 160px;
  }

  .donut-value {
    font-size: 1rem;
  }

  .donut-label {
    font-size: 0.75rem;
  }

  .viz-title {
    font-size: 0.9rem;
  }

  .legend-text,
  .legend-val {
    font-size: 0.8rem;
  }

  .legend-dot {
    width: 10px;
    height: 10px;
  }
}

@media (max-width: 480px) {
  .viz-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .power-grid {
    padding: 0.75rem;
  }

  .panel-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .donut-container {
    width: 140px;
    height: 140px;
  }

  .donut-value {
    font-size: 0.95rem;
  }

  .donut-label {
    font-size: 0.7rem;
  }

  .viz-title {
    font-size: 0.85rem;
  }

  .viz-legend {
    gap: 0.3rem;
  }

  .legend-text,
  .legend-val {
    font-size: 0.75rem;
  }

  .legend-dot {
    width: 9px;
    height: 9px;
  }

  .legend-item {
    gap: 0.3rem;
  }
}
</style>
